import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Sparkles, Loader2, Plus } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { fmtCurrency } from '../../lib/localeUtils';

export const POSAISearchDialog = ({ open, onOpenChange, initialQuery, onAddToCart }) => {
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);

  useEffect(() => {
    if (open) { setQuery(initialQuery || ''); setResults(null); }
  }, [open, initialQuery]);

  const runSearch = async () => {
    if (!query.trim() || loading) return;
    setLoading(true);
    setResults(null);
    try {
      const { data } = await api.post('/ai/pos-search', { query: query.trim() });
      setResults(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'AI search failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="ai-search-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-violet-600" /> AI Product Search
          </DialogTitle>
          <DialogDescription className="text-xs">
            Type a symptom, condition, or generic name. Examples: "headache reliever",
            "diarrhoea medicine for a 5-year-old", "amlodipine alternative".
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="flex gap-2">
            <Input
              placeholder="What does the customer need?"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') runSearch(); }}
              autoFocus
              data-testid="ai-search-query-input"
            />
            <Button
              onClick={runSearch}
              disabled={loading || !query.trim()}
              className="bg-violet-600 hover:bg-violet-700 gap-1.5"
              data-testid="ai-search-go-btn"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
              Search
            </Button>
          </div>
          {results?.explanation && (
            <p className="text-xs text-muted-foreground italic px-1">{results.explanation}</p>
          )}
          {loading ? (
            <div className="py-12 text-center text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin mx-auto mb-2" />
              <p className="text-xs">Reasoning over your catalog…</p>
            </div>
          ) : results ? (
            results.matches?.length === 0 ? (
              <div className="py-12 text-center text-muted-foreground text-xs" data-testid="ai-search-no-matches">
                No matches in your current stock.
              </div>
            ) : (
              <ul className="divide-y divide-border max-h-[420px] overflow-y-auto rounded-lg border bg-card">
                {results.matches.map((m) => (
                  <li
                    key={m.product_id}
                    className="px-3 py-2.5 hover:bg-violet-50/40 dark:hover:bg-violet-950/20 transition-colors"
                    data-testid={`ai-match-${m.product_id}`}
                  >
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold truncate">{m.name}</p>
                        <p className="text-[11px] text-muted-foreground">
                          {m.generic_name || '—'} {m.requires_prescription && <span className="ml-1 text-blue-600">· Rx</span>}
                        </p>
                      </div>
                      <div className="text-right shrink-0">
                        <p className="text-sm font-bold tabular-nums">{fmtCurrency(m.unit_price)}</p>
                        <p className="text-[10px] text-muted-foreground">{m.stock_quantity} in stock</p>
                      </div>
                    </div>
                    <p className="text-[11px] text-violet-700 dark:text-violet-400 leading-snug">
                      <Sparkles className="h-2.5 w-2.5 inline mr-1" />
                      {m.reason}
                    </p>
                    <Button
                      size="sm"
                      className="mt-2 h-7 text-xs gap-1 bg-violet-600 hover:bg-violet-700"
                      onClick={() => {
                        onAddToCart(m);
                        onOpenChange(false);
                        toast.success(`Added ${m.name}`);
                      }}
                      data-testid={`ai-match-add-${m.product_id}`}
                    >
                      <Plus className="h-3 w-3" /> Add to cart
                    </Button>
                  </li>
                ))}
              </ul>
            )
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
};
